import { differenceInCalendarDays, differenceInCalendarMonths, differenceInCalendarWeeks, differenceInCalendarYears, differenceInSeconds, differenceInYears, addDays as dnsAddDays, addMonths as dnsAddMonths, addYears as dnsAddYears, differenceInDays as dnsDifferenceInDays, differenceInMinutes as dnsDifferenceInMinutes, format as dnsFormat, formatISO as dnsFormatISO, getYear as dnsGetYear, isAfter as dnsIsAfter, isBefore as dnsIsBefore, isFirstDayOfMonth as dnsIsFirstDayOfMonth, isSameDay as dnsIsSameDay, lastDayOfMonth as dnsLastDayOfMonth, parse as dnsParse, startOfDay as dnsStartOfDay, startOfMonth as dnsStartOfMonth, startOfToday as dnsStartOfToday, startOfTomorrow as dnsStartOfTomorrow, subDays as dnsSubDays, subMonths as dnsSubMonths, subYears as dnsSubYears, isSameSecond, parseISO, } from 'date-fns';
export const createDateRange = (yearEndLimit, yearStartLimit = 0) => {
    const start = new Date(dnsSubYears(new Date(), yearEndLimit));
    const startDate = new Date(dnsSubDays(start, 1));
    // endDate is today's date by default
    const endDate = new Date(dnsAddYears(new Date(), yearStartLimit));
    return { startDate, endDate };
};
export const toDate = (date) => typeof date === 'string' ? parseISO(date) : date;
export const formatCompleteISO = (date) => dnsFormatISO(date, { representation: 'complete' });
export const formatDateOnly = (date) => dnsFormatISO(date, { representation: 'date' });
/**
 * format a date (string) into UTC Date: DD.MM.YYYY
 * NOTE: this does not take the timezone into account (with timezone use formatUTCDateStrToLocalDate)
 *
 * @param dateString
 * @returns
 */
export const formatUtcDateOnly = (dateString) => {
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${day}.${month}.${year}`;
};
/**
 * format a date (string) into UTC Time: HH:MM
 * NOTE: this does not take the timezone into account (with timezone use formatUTCDateStrToLocalTime)
 *
 * @param dateString
 * @returns
 */
export const formatUtcTimeOnly = (dateString) => {
    const date = new Date(dateString);
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};
/**
 * format UTC date to local date
 * e.g. 2025-01-13T09:41:53.698Z with de-DE becomes 13.01.2025
 * @param dateString
 * @param locale
 * @returns
 */
export const formatUTCDateStrToLocalDate = (dateString, locale = 'de-DE') => {
    return new Date(dateString).toLocaleDateString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
};
/**
 * format date to local time HH:MM
 * e.g. 2025-01-13T09:41:53.698Z with de-DE becomes 10:41
 * @param dateString
 * @param locale
 * @returns
 */
export const formatUTCDateStrToLocalTime = (dateString, locale = 'de-DE') => {
    return new Date(dateString).toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
    });
};
/**
 * format date to local time HH:MM
 * e.g. 2025-01-13T09:41:53.698Z with de-DE becomes 13.01.2025, 10:41
 * @param dateString
 * @param locale
 * @returns
 */
export const formatUTCDateStrToLocalDateTime = (dateString, locale = 'de-DE') => {
    return new Date(dateString).toLocaleString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};
export const formatTimeOnly = (date) => dnsFormatISO(date, { representation: 'time' });
export const formatCustomDate = (date, format) => dnsFormat(new Date(date), format);
export const parseDateStringToDate = (date, format, referenceDate) => dnsParse(date, format, referenceDate ?? new Date());
/**
 *
 * @param date1
 * @param date2
 * @return date1 - date2 in dates
 */
export const diffInDays = (date1, date2) => differenceInCalendarDays(toDate(date1), toDate(date2));
/**
 *
 * @param date1 the later date
 * @param date2 the earlier date
 * @return date1 - date2 in years
 */
export const diffInYears = (date1, date2) => differenceInCalendarYears(toDate(date1), toDate(date2));
export const getYear = (date) => dnsGetYear(date);
/**
 *
 * @param date1
 * @param date2
 * @return date1 - date2 in years as number
 */
export const diffInYearsAsNumber = (date1, date2) => differenceInYears(date1, date2);
export const diffInMinutes = (date1, date2) => dnsDifferenceInMinutes(date1, date2);
/**
 *
 * @param date1
 * @param date2
 * @return date1 - date2 in years as number but rounds up to next year e.g. 30.06.2023 - 29.06.2022 would be 2
 */
export const diffInYearsRoundUpToNextYear = (date1, date2) => differenceInYears(date1, date2) + calcRoundUpToNextYear(date1, date2);
/**
 * helper for diffInYearsRoundUpToNextYear
 * @param date1
 * @param date2
 * @returns
 */
const calcRoundUpToNextYear = (date1, date2) => date2.getMonth() < date1.getMonth() ||
    (date2.getMonth() === date1.getMonth() && date2.getDate() < date1.getDate())
    ? 1
    : 0;
/**
 *
 * @param date1
 * @param date2
 * @return date1 - date2 in months
 */
export const diffInMonths = (date1, date2) => differenceInCalendarMonths(toDate(date1), toDate(date2));
/**
 *
 * @param date1
 * @param date2
 * @return date1 - date2 in weeks
 */
export const diffInWeeks = (date1, date2) => differenceInCalendarWeeks(toDate(date1), toDate(date2));
/**
 *
 * @param d1 the date that should be after the other one to return true
 * @param d2 the date to compare with
 * @return date1 - date2 in weeks
 */
export const isAfter = (d1, d2) => dnsIsAfter(toDate(d1), toDate(d2));
/**
 * `isBefore` takes a date to compare (d1), i.e. today's date, which will be checked against
 * another date (d2), i.e. an insurance end date.
 *
 * @param d1
 * @param d2
 * @returns
 */
export const isBefore = (d1, d2) => dnsIsBefore(toDate(d1), toDate(d2));
export const isSameDay = (d1, d2) => dnsIsSameDay(toDate(d1), toDate(d2));
export const sameOrBefore = (d1 = new Date(), d2 = new Date()) => isSameSecond(d1, d2) || !!isBefore(d1, d2);
export const sameOrAfter = (d1 = new Date(), d2 = new Date()) => isSameSecond(d1, d2) || !!isAfter(d1, d2);
export const isWithinXSeconds = (d1 = new Date(), d2 = new Date(), seconds) => Math.abs(differenceInSeconds(d1, d2)) <= seconds;
export const isDateInRange = (date, range) => sameOrBefore(date, range.to) && sameOrAfter(date, range.from);
/**
 *
 * @param date
 * @param yearsToAdd
 * @return date1 + yearsToAdd
 */
export const addYears = (date, yearsToAdd) => dnsAddYears(date, yearsToAdd);
/**
 *
 * @param date
 * @param yearsToSub
 * @return date1 - yearsToSub
 */
export const subYears = (date, yearsToSub) => dnsSubYears(date, yearsToSub);
/**
 *
 * @param date
 * @param monthsToAdd
 * @return date1 + monthsToAdd
 */
export const addMonths = (date, monthsToAdd) => dnsAddMonths(date, monthsToAdd);
/**
 *
 * @param date1
 * @param monthToSub
 * @return date - monthToSub
 */
export const subMonths = (date, monthToSub) => dnsSubMonths(date, monthToSub);
/**
 *
 * @param date
 * @param daysToAdd
 * @return date + daysToAdd
 */
export const addDays = (date, daysToAdd) => dnsAddDays(date, daysToAdd);
/**
 *
 * @param date
 * @param daysToSub
 * @return date - daysToSub
 */
export const subDays = (date, daysToSub) => dnsSubDays(date, daysToSub);
/**
 *
 * @param dateLeft
 * @param dateRight
 * @return number
 */
export const diffDays = (dateLeft, dateRight) => dnsDifferenceInDays(dateLeft, dateRight);
/**
 *
 * @return start of tomorrow
 */
export const startOfTomorrow = () => dnsStartOfTomorrow();
/**
 *
 * @return start of today
 */
export const startOfToday = () => dnsStartOfToday();
/**
 * @param date
 * @return start of tomorrow
 */
export const isFirstDayOfMonth = (date) => dnsIsFirstDayOfMonth(date);
/**
 *
 * @param date
 * @return start of month
 */
export const startOfMonth = (date) => dnsStartOfMonth(date);
/**
 *
 * @param date
 * @return end of month
 */
export const lastDayOfMonth = (date) => dnsLastDayOfMonth(date);
/**
 *
 * @param date
 * @return start of day
 */
export const startOfDay = (date) => dnsStartOfDay(date);
/**
 *
 * @param time
 * @returns create new Date
 */
export const createDate = (time) => time ? new Date(time) : new Date();
export const startOfDate = (date) => new Date(date.setHours(0, 0, 0, 0));
export const endOfDate = (date) => new Date(date.setHours(23, 59, 59, 999));
/**
 *
 * @param date
 * @returns true if given date is in the past
 */
export const isDateInPast = (date) => isAfter(createDate(), date);
/**
 *
 * @param date
 * @returns true if given date is in the future
 */
export const isDateInFuture = (date) => isBefore(createDate(), date);
/**
 * `isDateSameOrAfterPastDate` is used to determine whether
 * two dates are exactly x months apart or less. For instance, 01.01.2023
 * is exactly 18 months after 01.06.2021 and would return `true`, and
 * 02.01.2023 returns `true`. However 31.05.2021 would return `false`.
 *
 * @param startDate
 * @param endDate
 * @param months
 * @returns boolean
 */
export const isDateSameOrAfterPastDate = (startDate, endDate, months) => {
    if (!endDate) {
        return false;
    }
    const maxDateInPast = subMonths(createDate(startDate), months);
    return isSameDay(endDate, maxDateInPast) || isAfter(endDate, maxDateInPast);
};
export const getUTZDateStrFromDateStr = (dateStr) => `${dateStr.slice(0, 10)}T00:00:00.000Z`;
export const getFormatedDateStrFromDateStr = (dateStr) => {
    if (!dateStr) {
        return '';
    }
    const dateParts = dateStr.split('.');
    const day = dateParts[0].padStart(2, '0');
    const month = dateParts[1].padStart(2, '0');
    const year = dateParts[2];
    return `${year}-${month}-${day}T00:00:00.000Z`;
};
